import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { siteSettingsAPI, reviewsAPI } from '../utils/api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

function AdminSiteSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState({
    about_title: '',
    about_text: '',
    instagram_embed: '',
    business_hours: {},
    phone_number: '',
    email_address: '',
    address: '',
  });
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchReviews();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await siteSettingsAPI.getAll();
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await reviewsAPI.getAllAdmin();
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await siteSettingsAPI.update(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleHoursChange = (day, field, value) => {
    setSettings((prev) => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleToggleReview = async (id) => {
    try {
      await reviewsAPI.toggle(id);
      fetchReviews();
      toast.success('Review updated');
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await reviewsAPI.delete(id);
      fetchReviews();
      toast.success('Review deleted');
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h2>Site Settings</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Configure your landing page content
      </p>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            minWidth: { xs: 'auto', sm: 120 },
            px: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Tab icon={<InfoIcon />} label="About" iconPosition="start" />
        <Tab icon={<ScheduleIcon />} label="Hours" iconPosition="start" />
        <Tab icon={<InstagramIcon />} label="Social" iconPosition="start" />
        <Tab icon={<StarIcon />} label="Reviews" iconPosition="start" />
      </Tabs>

      {/* About & Contact Tab */}
      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              About Section
            </Typography>

            <TextField
              fullWidth
              label="Section Title"
              value={settings.about_title}
              onChange={(e) => setSettings({ ...settings, about_title: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Welcome to Studio Jen"
            />

            <TextField
              fullWidth
              label="About Text"
              value={settings.about_text}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              multiline
              rows={4}
              sx={{ mb: 3 }}
              placeholder="Tell customers about your business..."
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={settings.phone_number}
                  onChange={(e) => setSettings({ ...settings, phone_number: e.target.value })}
                  placeholder="07xxx xxxxxx"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={settings.email_address}
                  onChange={(e) => setSettings({ ...settings, email_address: e.target.value })}
                  placeholder="hello@studiojen.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="123 High Street, London, SW1 1AA"
                />
              </Grid>
            </Grid>

            <Box mt={3}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Business Hours Tab */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Opening Hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set your regular business hours. These will be displayed on your landing page.
            </Typography>

            {DAYS.map((day) => (
              <Box
                key={day}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid #eee',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: 200 },
                  }}
                >
                  <Typography sx={{ fontWeight: 500, minWidth: 90 }}>
                    {DAY_LABELS[day]}
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!settings.business_hours[day]?.closed}
                        onChange={(e) =>
                          handleHoursChange(day, 'closed', !e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={settings.business_hours[day]?.closed ? 'Closed' : 'Open'}
                    sx={{ mr: 0 }}
                  />
                </Box>

                {!settings.business_hours[day]?.closed && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                      pl: { xs: 0, sm: 0 },
                    }}
                  >
                    <TextField
                      type="time"
                      label="Open"
                      value={settings.business_hours[day]?.open || '09:00'}
                      onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1, maxWidth: 130 }}
                    />
                    <Typography sx={{ color: 'text.secondary' }}>to</Typography>
                    <TextField
                      type="time"
                      label="Close"
                      value={settings.business_hours[day]?.close || '17:00'}
                      onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1, maxWidth: 130 }}
                    />
                  </Box>
                )}
              </Box>
            ))}

            <Box mt={3}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Hours'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Social Media Tab */}
      {tab === 2 && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <InstagramIcon color="secondary" />
              <Typography variant="h6">Instagram Feed Embed</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste your LightWidget embed code below to display your Instagram feed on the landing page.
              Get your free embed code at{' '}
              <a href="https://lightwidget.com" target="_blank" rel="noopener noreferrer">
                lightwidget.com
              </a>
            </Typography>

            <TextField
              fullWidth
              label="Instagram Embed Code"
              value={settings.instagram_embed}
              onChange={(e) => setSettings({ ...settings, instagram_embed: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={4}
              placeholder='<script src="https://cdn.lightwidget.com/widgets/lightwidget.js"></script><iframe src="//lightwidget.com/widgets/..." ...'
              helperText="Paste the full embed code from LightWidget (includes script and iframe)"
            />

            {settings.instagram_embed && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Embed code saved! Your Instagram feed will appear on the landing page.
              </Alert>
            )}

            <Box mt={3}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Reviews Tab */}
      {tab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Customer Reviews
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reviews are auto-published. You can hide or delete reviews here.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  try {
                    const response = await reviewsAPI.importFresha();
                    toast.success(response.data.message);
                    fetchReviews();
                  } catch (error) {
                    toast.error('Failed to import reviews');
                  }
                }}
              >
                Import Fresha Reviews
              </Button>
            </Box>

            {reviewsLoading ? (
              <CircularProgress size={24} />
            ) : reviews.length === 0 ? (
              <Alert severity="info">No reviews yet</Alert>
            ) : (
              reviews.map((review) => (
                <Box
                  key={review.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: review.active ? 'divider' : 'error.light',
                    borderRadius: 1,
                    bgcolor: review.active ? 'background.paper' : 'action.disabledBackground',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', sm: 'flex-start' },
                      gap: { xs: 2, sm: 0 },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {review.customer_name}
                        {!review.active && (
                          <Typography component="span" color="error" sx={{ ml: 1 }}>
                            (Hidden)
                          </Typography>
                        )}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <StarIcon
                            key={star}
                            sx={{
                              fontSize: 16,
                              color: star <= review.rating ? '#FFD700' : '#ddd',
                            }}
                          />
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {new Date(review.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {review.review_text || '(No comment)'}
                      </Typography>
                      {review.service_name && (
                        <Typography variant="caption" color="primary">
                          Service: {review.service_name}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexShrink: 0,
                        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color={review.active ? 'warning' : 'success'}
                        onClick={() => handleToggleReview(review.id)}
                      >
                        {review.active ? 'Hide' : 'Show'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteReview(review.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AdminSiteSettings;
